import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  FileText,
  Settings,
  Edit,
  HelpCircle,
  MoreHorizontal,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../ui/Button';
import Select from '../../../ui/Select';
import CollapsibleSection from '../../../ui/CollapsibleSection';
import ConditionsCard from '../common/ConditionsCard';
import { Condition } from '../../../../types/segments/segments.types';
import { Node } from '@xyflow/react';
import { WorkflowNode } from 'types';
import { getEmailTemplateRequest } from '../../../../store/emailTemplates/emailTemplates.action';
import {
  selectEmailTemplateById,
  selectEmailTemplateLoadingById,
} from '../../../../store/emailTemplates/emailTemplates.selector';
import {
  getUnsubscribeGroupsRequest,
  selectUnsubscribeGroups,
} from 'store/unsubscribeGroups';
import { sanitizeHtmlForPreview } from '../../../../utils/helper';

interface EmailPanelProps {
  onClose: () => void;
  onSave: (emailData: any) => void;
  conditions?: { [key: string]: Condition[] };
  onConditionsChange?: (conditions: { [key: string]: Condition[] }) => void;
  conditionsModified?: boolean;
  campaignId?: string;
  selectedNode?: Node;
}

const EmailPanel: React.FC<EmailPanelProps> = ({
  onClose,
  onSave,
  conditions = {},
  onConditionsChange,
  conditionsModified = false,
  campaignId,
  selectedNode,
}) => {
  const dispatch = useDispatch();
  // Get initial config from selectedNode
  const nodeConfig = selectedNode
    ? (selectedNode as WorkflowNode).data?.config
    : null;

  const [trackOpens] = useState(nodeConfig?.trackOpens ?? true);
  const [sendingBehavior, setSendingBehavior] = useState(
    nodeConfig?.sendingBehavior ?? 'queue_draft'
  );
  const [subscriptionPreference, setSubscriptionPreference] = useState(
    nodeConfig?.subscriptionPreference ?? 'use_campaign_settings'
  );
  const [messageLimit] = useState(
    nodeConfig?.messageLimit ?? 'use_campaign_settings'
  );
  const [autoRetrySettings] = useState(
    nodeConfig?.autoRetrySettings ?? 'use_campaign_settings'
  );
  const [showConditionsCard, setShowConditionsCard] = useState(false);
  const [customRetryValue] = useState(nodeConfig?.customRetryValue ?? 1);
  const [customRetryUnit] = useState(nodeConfig?.customRetryUnit ?? 'hour');
  const [localConditions, setLocalConditions] = useState<{
    [key: string]: Condition[];
  }>(conditions);
  const [emailSettingsModified, setEmailSettingsModified] = useState(false);

  // Get templateId from selectedNode config
  const templateId = nodeConfig?.templateId || null;

  const emailTemplateSelector = useMemo(
    () => (templateId ? selectEmailTemplateById(templateId) : () => null),
    [templateId]
  );
  const emailTemplateLoadingSelector = useMemo(
    () =>
      templateId ? selectEmailTemplateLoadingById(templateId) : () => false,
    [templateId]
  );

  const selectedEmailTemplate = useSelector(emailTemplateSelector);
  const emailTemplateLoading = useSelector(emailTemplateLoadingSelector);
  const unsubscribeGroups = useSelector(selectUnsubscribeGroups);

  // Fetch email template when templateId is available
  useEffect(() => {
    if (templateId) {
      if (!selectedEmailTemplate) {
        dispatch(getEmailTemplateRequest(templateId));
      }
      dispatch(getUnsubscribeGroupsRequest());
    }
  }, [templateId, dispatch, selectedEmailTemplate]);

  const hasEmailTemplate = selectedEmailTemplate && templateId;

  const handleConditionsSave = (conditionsData: any) => {
    setLocalConditions(conditionsData.conditions);
    if (onConditionsChange) {
      onConditionsChange(conditionsData.conditions);
    }
    setShowConditionsCard(false);
  };

  const handleSave = () => {
    onSave({
      trackOpens,
      sendingBehavior,
      subscriptionPreference,
      messageLimit,
      autoRetrySettings,
      conditions: localConditions,
      templateId,
      customRetryValue,
      customRetryUnit,
    });
    setEmailSettingsModified(false);
  };

  const handleConditionsChange = (newConditions: {
    [key: string]: Condition[];
  }) => {
    // Only update local conditions for editing, don't trigger parent callback
    setLocalConditions(newConditions);
  };

  const nodeId = selectedNode ? (selectedNode as WorkflowNode).id : null;
  const contentPath =
    campaignId && nodeId
      ? `/campaigns/${campaignId}/node/${nodeId}/email-builder${templateId ? `/${templateId}` : ''}`
      : null;

  return (
    <div className="w-full flex">
      {showConditionsCard && (
        <ConditionsCard
          onClose={() => setShowConditionsCard(false)}
          onSave={handleConditionsSave}
          initialConditions={localConditions}
          onConditionsChange={handleConditionsChange}
        />
      )}
      <div className="flex flex-col bg-card rounded-lg shadow-sm border border-border w-[30vw] h-[calc(85vh-100px)] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Email</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Send a message in this step
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {/* Content Section */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <FileText className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-medium text-foreground">Content</h3>
              </div>

              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/40 transition-colors duration-200 relative overflow-hidden bg-muted/30"
                style={{ minHeight: '120px', maxHeight: '200px' }}
              >
                {/* Email HTML Preview - Behind the UI */}
                {hasEmailTemplate &&
                  selectedEmailTemplate?.unlayerConfig?.html && (
                    <div
                      className="absolute inset-0 overflow-hidden opacity-60 pointer-events-none"
                      style={{ padding: '0.5rem' }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtmlForPreview(
                            selectedEmailTemplate.unlayerConfig.html
                          ),
                        }}
                        style={{
                          transform: 'scale(0.55)',
                          transformOrigin: 'top left',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </div>
                  )}

                <div className="flex flex-col items-center justify-center h-full space-y-3 relative z-10">
                  <div className="p-3 rounded-full bg-muted shadow-sm">
                    <FileText className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div className="space-y-1">
                    {emailTemplateLoading ? (
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse w-32 mx-auto" />
                        <div className="h-3 bg-muted rounded animate-pulse w-24 mx-auto" />
                      </div>
                    ) : hasEmailTemplate ? (
                      <p className="text-sm font-medium text-foreground">
                        {selectedEmailTemplate.name}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-foreground">
                          No email content yet
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Create your email using the visual editor
                        </p>
                      </>
                    )}
                  </div>
                  {emailTemplateLoading ? (
                    <div className="h-9 bg-muted rounded animate-pulse w-32" />
                  ) : (
                    contentPath && (
                      <Link
                        to={contentPath}
                        className="inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary-hover focus:outline-none focus:ring-0 px-4 py-2 text-sm"
                      >
                        <Edit className="w-4 h-4" />
                        {hasEmailTemplate ? 'Edit Content' : 'Create Content'}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Settings Section */}
            <CollapsibleSection
              title="Settings"
              icon={Settings}
              iconColor="text-primary"
              iconBgColor="bg-primary/10"
              defaultExpanded={true}
              showActionButton={true}
              actionButton={
                <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              }
            >
              <div className="space-y-3">
                {/* Track Opens Checkbox */}
                {/* <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="track-opens"
                    checked={trackOpens}
                    onChange={(e) => {
                      setTrackOpens(e.target.checked);
                      setEmailSettingsModified(true);
                    }}
                    className="w-4 h-4 rounded border-border accent-primary focus:ring-0"
                  />
                  <label
                    htmlFor="track-opens"
                    className="text-sm text-foreground"
                  >
                    Track opens and link clicks in this message
                  </label>
                </div> */}

                {/* Sending Behavior */}
                <div className="space-y-3">
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Sending Behavior
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 p-2 rounded-lg bg-muted/50 border border-border">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Select
                        value={sendingBehavior}
                        onChange={(e) => {
                          setSendingBehavior(e.target.value);
                          setEmailSettingsModified(true);
                        }}
                        options={[
                          {
                            value: 'send_automatically',
                            label: 'Send Automatically',
                          },
                          { value: 'queue_draft', label: 'Queue Draft' },
                          { value: 'do_not_send', label: "Don't Send" },
                        ]}
                        className="w-full text-sm border-input bg-background focus:ring-1 focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                  {(sendingBehavior === 'queue_draft' ||
                    sendingBehavior === 'do_not_send') && (
                    <p className="text-xs text-destructive">
                      {sendingBehavior === 'queue_draft'
                        ? "Email will be saved as draft and won't be sent automatically."
                        : 'Email will not be sent.'}
                    </p>
                  )}
                </div>

                {/* Subscription Preference */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Subscription Preference
                    </label>
                    <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <Select
                    value={subscriptionPreference}
                    onChange={(e) => {
                      setSubscriptionPreference(e.target.value);
                      setEmailSettingsModified(true);
                    }}
                    options={{
                      ...(unsubscribeGroups.length > 0 && {
                        Topics: unsubscribeGroups.map((group) => ({
                          value: group._id || '',
                          label: group.title,
                        })),
                      }),
                      Global: [
                        {
                          value: 'use_campaign_settings',
                          label: 'Use campaign settings',
                        },
                        {
                          value: 'subscribed',
                          label: 'All subscribed people',
                        },
                        {
                          value: 'all',
                          label: 'All subscribed and unsubscribed',
                        },
                      ],
                    }}
                    className="w-full text-sm"
                  />
                </div>

                {/* Message Limit */}
                {/* <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Message Limit
                    </label>
                    <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <Select
                    value={messageLimit}
                    onChange={(e) => {
                      setMessageLimit(e.target.value);
                      setEmailSettingsModified(true);
                    }}
                    options={[
                      {
                        value: 'use_campaign_settings',
                        label: 'Use campaign settings',
                      },
                      {
                        value: 'do_not_count_toward_limit',
                        label: "Don't count toward limit",
                      },
                      {
                        value: 'count_toward_limit',
                        label: 'Count toward limit',
                      },
                    ]}
                    className="w-full text-sm"
                  />
                </div> */}

                {/* Auto-Retry Settings */}
                {/* <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Auto-Retry Settings
                    </label>
                    <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <Select
                    value={autoRetrySettings}
                    onChange={(e) => {
                      setAutoRetrySettings(e.target.value);
                      setCustomRetryValue(0);
                      setCustomRetryUnit('hour');
                      setEmailSettingsModified(true);
                    }}
                    disabled={!isAutoRetryEnabled}
                    options={[
                      {
                        value: 'use_campaign_settings',
                        label: 'Use campaign settings',
                      },
                      {
                        value: 'do_not_retry_messages',
                        label: "Don't retry messages",
                      },
                      { value: 'custom_window', label: 'Set a custom window' },
                    ]}
                    className={`w-full text-sm ${!isAutoRetryEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />

                  {showCustomWindowInputs && (
                    <div className="space-y-2 mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">
                        If the limit is reached, retry this message for up to
                        (max 48 hours):
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={customRetryValue}
                          onChange={(e) => {
                            if (
                              customRetryUnit === 'minutes' &&
                              Number(e.target.value) > 2880
                            ) {
                              setCustomRetryValue(2880);
                            } else if (
                              customRetryUnit === 'hour' &&
                              Number(e.target.value) > 48
                            ) {
                              setCustomRetryValue(48);
                            } else {
                              setCustomRetryValue(Number(e.target.value));
                            }
                            setEmailSettingsModified(true);
                          }}
                          className="w-20 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          min={1}
                          max={customRetryUnit === 'minutes' ? 2880 : 48}
                        />
                        <Select
                          value={customRetryUnit}
                          onChange={(e) => {
                            setCustomRetryUnit(e.target.value);
                            setEmailSettingsModified(true);
                          }}
                          options={[
                            { value: 'minutes', label: 'minutes' },
                            { value: 'hour', label: 'hour' },
                          ]}
                          className="w-24 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div> */}
              </div>
            </CollapsibleSection>

            {/* Conditions Section */}
            {/* <CollapsibleSection
              title="Conditions"
              icon={Filter}
              iconColor="text-primary"
              iconBgColor="bg-primary/10"
              defaultExpanded={hasConditions}
              showActionButton={true}
              actionButton={
                <button
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  onClick={() => setShowConditionsCard(true)}
                >
                  <Plus className="w-4 h-4" />
                </button>
              }
            >
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Check whether people meet specific conditions before they
                  enter the delay or perform the action.
                </p>

                {hasConditions && (
                  <div className="p-3 rounded-lg border border-border bg-muted/30">
                    <ConditionDisplay conditions={conditions} />
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={() => setShowConditionsCard(true)}
                  className="w-full text-foreground border-border hover:bg-muted"
                >
                  {hasConditions ? 'Edit conditions' : 'Add condition'}
                </Button>
              </div>
            </CollapsibleSection> */}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border shrink-0 space-y-3">
            <Button
              variant="primary"
              disabled={!conditionsModified && !emailSettingsModified}
              onClick={handleSave}
              className="w-full"
            >
              Save and build workflow
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPanel;
